"use client";

import { useRef, useState } from "react";
import Image from "next/image";
import { MetaRow } from "@/components/shared/main/meta-row";
import { HOVER_IMAGE, HOVER_TITLE, TAP_LINK } from "@/lib/theme/interaction";
import {
  ARTICLE_DETAIL_ROUTE,
  FEATURE_DETAIL_ROUTE,
  categoryRoute,
} from "@/lib/routes";
import { ScrollEdgeArrows, useScrollEdges } from "@/components/shared/main/scroll-edges";

/**
 * 信報專題 — a sponsored "featured topic" showcase: section title + horizontal
 * pill tabs + wide banner + italic tagline + a 2/3 lead (28px) and 1/3 rail of
 * headlines. Extracted from the home featured-topic block so the layout is
 * reusable; all copy/imagery is supplied by the caller.
 *
 * The tabs are a real control, not decoration — QA: "the tabs should be
 * clickable, and when click on to it the content below will change - banner and
 * articles". Each topic therefore carries its OWN banner/tagline/feature/rail,
 * and the component holds the selected index. Everything else on the panel
 * (banner, tagline, headlines, excerpt) navigates: the topic chrome to the
 * feature detail page, the article copy to the article.
 */

export type ShowcaseSideItem = { title: string; time: string; href?: string };

export type ShowcaseTopic = {
  label: string;
  banner: { src: string; alt: string };
  tagline: { label: string; title: string };
  feature: { title: string; excerpt: string; time: string; href?: string };
  side: ShowcaseSideItem[];
  /** The topic's own feature page; defaults to `FEATURE_DETAIL_ROUTE`. */
  href?: string;
};

export type FeaturedTopicShowcaseProps = {
  title: string;
  topics: ShowcaseTopic[];
};

const sectionTitleStyle: React.CSSProperties = {
  fontFamily: "Roboto, 'Noto Sans TC', sans-serif",
  fontSize: "20px",
  fontWeight: 400,
  lineHeight: "normal",
  color: "rgba(0,0,0,0.5)",
  textTransform: "uppercase",
};

/**
 * The section label, linked to its own listing.
 *
 * Same rule the two section headers already follow: the destination is derived
 * from the label, and a label with no listing stays a plain `<h2>` rather than
 * a dead link. QA: 信報專題 should open /features.
 */
function SectionTitle({ title }: { title: string }) {
  const href = categoryRoute(title);
  if (!href) return <h2 style={sectionTitleStyle}>{title}</h2>;
  return (
    <a href={href} className={`${TAP_LINK} w-fit block`}>
      <h2 style={sectionTitleStyle} className={HOVER_TITLE}>
        {title}
      </h2>
    </a>
  );
}

const taglineSerif: React.CSSProperties = {
  fontFamily: "Merriweather, Georgia, serif",
  fontStyle: "italic",
  fontSize: "15px",
  color: "#0e8f63",
};

export function FeaturedTopicShowcase({
  title,
  topics,
}: FeaturedTopicShowcaseProps) {
  const [active, setActive] = useState(0);
  const tabsRef = useRef<HTMLDivElement | null>(null);
  const tabEdges = useScrollEdges(tabsRef);
  const topic = topics[active] ?? topics[0];
  if (!topic) return null;

  const { banner, tagline, feature, side } = topic;
  const topicHref = topic.href ?? FEATURE_DETAIL_ROUTE;

  return (
    <div className="pt-[16px]" style={{ borderTop: "1px solid #000" }}>
      <div className="pb-[24px]">
        <SectionTitle title={title} />
      </div>

      {/* Tab strip — single-line, overflows horizontally (matches Figma). The
          scrollbar is hidden, so it carries the shared ‹ / › affordances. */}
      <div className="relative">
      <div
        ref={tabsRef}
        role="tablist"
        aria-label={title}
        className="flex items-center gap-[8px] pb-[24px] overflow-x-auto no-scrollbar"
      >
        {topics.map((t, i) => {
          const selected = i === active;
          return (
            <button
              key={t.label}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => setActive(i)}
              className="rounded-full shrink-0 transition-colors hover:bg-[rgba(9,99,68,0.18)]"
              style={{
                padding: "11px 13px",
                border: selected
                  ? "1px solid #000"
                  : "1px solid rgba(0,0,0,0)",
                backgroundColor: "rgba(9,99,68,0.1)",
                fontFamily:
                  "'Roboto Condensed', Roboto, 'Noto Sans TC', sans-serif",
                fontSize: "16px",
                color: "#000",
                lineHeight: "normal",
                whiteSpace: "nowrap",
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>
        <ScrollEdgeArrows scroller={tabsRef} edges={tabEdges} className="-mt-[12px]" />
      </div>

      {/* Banner 868/260 — opens the topic's feature page */}
      <a
        href={topicHref}
        className={`relative block w-full overflow-hidden bg-[#f5f5f7] mb-[24px] ${TAP_LINK}`}
        style={{ aspectRatio: "868 / 260" }}
      >
        <Image
          src={banner.src}
          alt={banner.alt}
          fill
          className={`object-contain object-center ${HOVER_IMAGE}`}
          sizes="(max-width: 1024px) 100vw, 696px"
        />
      </a>

      {/* Tag line — "信報專題 中銀人壽旅居退休攻略", also the topic link */}
      <a
        href={topicHref}
        className={`flex w-fit items-center gap-[8px] pb-[24px] ${TAP_LINK}`}
      >
        <span
          className={`pb-[3px] ${HOVER_TITLE}`}
          style={{ ...taglineSerif, borderBottom: "1px dashed #0e8f63" }}
        >
          {tagline.label}
        </span>
        <span
          className={HOVER_TITLE}
          style={{
            fontFamily: "Merriweather, Georgia, serif",
            fontStyle: "italic",
            fontWeight: 700,
            fontSize: "15px",
            color: "#0e8f63",
          }}
        >
          {tagline.title}
        </span>
      </a>

      {/* Grid 2/3 + 1/3 */}
      <div className="grid w-full grid-cols-1 md:grid-cols-3 gap-x-[32px] gap-y-[24px]">
        {/* Left col span 2 — 28px lead */}
        <div className="md:col-[1/span_2] flex flex-col gap-[12px] pb-[5.98px] max-md:pb-[24px] max-md:border-b max-md:border-[rgba(0,0,0,0.1)]">
          <a
            href={feature.href ?? ARTICLE_DETAIL_ROUTE}
            className={TAP_LINK}
          >
            <h3
              className={`${HOVER_TITLE} line-clamp-2 overflow-hidden text-ellipsis`}
              style={{
                fontFamily: "Roboto, 'Noto Sans TC', sans-serif",
                fontSize: "28px",
                fontWeight: 500,
                lineHeight: "39.2px",
                color: "#000",
              }}
            >
              {feature.title}
            </h3>
          </a>
          <a
            href={feature.href ?? ARTICLE_DETAIL_ROUTE}
            className={`${TAP_LINK} block`}
          >
            <p
              className="overflow-hidden text-ellipsis line-clamp-3"
              style={{
                fontFamily: "Roboto, 'Noto Sans TC', sans-serif",
                fontSize: "14px",
                lineHeight: "18.2px",
                color: "#6f6f6f",
              }}
            >
              {feature.excerpt}
            </p>
          </a>
          <MetaRow
            time={feature.time}
            href={feature.href ?? ARTICLE_DETAIL_ROUTE}
          />
        </div>

        {/* Right col — headline-only rail */}
        <div className="flex flex-col">
          {side.map((s, i) => (
            <div
              key={s.title}
              className={
                i === 0
                  ? "pb-[21px] flex flex-col gap-[12px]"
                  : "pt-[20px] flex flex-col gap-[12px]"
              }
              style={
                i === 0
                  ? { borderBottom: "1px solid rgba(0,0,0,0.1)" }
                  : undefined
              }
            >
              <a href={s.href ?? ARTICLE_DETAIL_ROUTE} className={TAP_LINK}>
                <h4
                  className={`hkej-card-title-sm ${HOVER_TITLE} line-clamp-2 overflow-hidden text-ellipsis`}
                  style={{
                    fontFamily: "Roboto, 'Noto Sans TC', sans-serif",
                    fontSize: "15px",
                    fontWeight: 700,
                    lineHeight: "21px",
                    color: "#000",
                  }}
                >
                  {s.title}
                </h4>
              </a>
              <MetaRow time={s.time} href={s.href ?? ARTICLE_DETAIL_ROUTE} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
